import { API_BASE } from '../apiConfig';
import defaultTheme, { themeToCssVars } from './defaultTheme';
import { setResolvedTheme } from './themeStore';

const FETCH_TIMEOUT_MS = 2500;

// Loose client-side guards (the backend is authoritative and already strict —
// this is defence in depth so a bad value can never reach the DOM).
const CLIENT_HEX_RE = /^#[0-9a-fA-F]{3,8}$/;
const CLIENT_RGB_RE = /^rgba?\([\d.,\s%]+\)$/;
const isValidColor = (value) =>
  typeof value === 'string' && (CLIENT_HEX_RE.test(value.trim()) || CLIENT_RGB_RE.test(value.trim()));

const getPartnerSlug = () => {
  if (typeof window === 'undefined') return '';
  try {
    const slug = (new URLSearchParams(window.location.search).get('partner') || '')
      .trim()
      .toLowerCase();
    return /^[a-z0-9-]{1,40}$/.test(slug) ? slug : '';
  } catch (err) {
    return '';
  }
};

const fetchPartnerConfig = async (slug) => {
  if (typeof fetch !== 'function') return null;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;
  try {
    const response = await fetch(`${API_BASE}/partner-config?slug=${encodeURIComponent(slug)}`, {
      method: 'GET',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    });
    if (!response.ok) return null;
    const payload = await response.json();
    return payload && payload.partner ? payload.partner : null;
  } catch (err) {
    return null;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const buildResolvedTheme = (partner) => {
  if (!partner) return defaultTheme;

  const colors = { ...defaultTheme.colors };
  for (const [key, value] of Object.entries(partner.colors || {})) {
    if (key in colors && isValidColor(value)) {
      colors[key] = String(value).trim();
    }
  }

  const fonts = {
    body: (partner.fonts && partner.fonts.body) || defaultTheme.fonts.body,
    heading: (partner.fonts && partner.fonts.heading) || defaultTheme.fonts.heading
  };

  const mapPin = partner.pin && partner.pin.url
    ? {
      url: partner.pin.url,
      width: partner.pin.width || defaultTheme.mapPin.width,
      height: partner.pin.height || defaultTheme.mapPin.height
    }
    : { ...defaultTheme.mapPin };

  return {
    slug: partner.slug || null,
    colors,
    fonts,
    brand: {
      logoUrl: partner.logoUrl || defaultTheme.brand.logoUrl,
      logoAlt: partner.brandName || defaultTheme.brand.logoAlt,
      // Partners default to hiding the house nav, so this link is rarely shown;
      // when it is, point at the embed root rather than the house site.
      homeUrl: partner.logoUrl ? '/' : defaultTheme.brand.homeUrl
    },
    // We never render the house nav links (Home/Shop/Court Finder) for partners.
    navLinks: [],
    showGlobalNav: Boolean(partner.showGlobalNav),
    mapPin,
    showAds: partner.showAds !== false,
    adChannel: partner.adChannel || '',
    fontNames: {
      body: partner.fonts && partner.fonts.bodyName,
      heading: partner.fonts && partner.fonts.headingName
    }
  };
};

const applyCssVars = (theme) => {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const vars = themeToCssVars(theme);
  const root = document.documentElement;
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
};

// Load a curated font from Google Fonts (the only sources are our curated list,
// all of which are Google Fonts families). Inter/Montserrat are already loaded
// in index.html, but loading them again is harmless.
const loadFonts = (names) => {
  if (typeof document === 'undefined') return;
  const families = Array.from(new Set((names || []).filter(Boolean)));
  if (!families.length) return;
  if (document.querySelector('link[data-partner-font="1"]')) return;
  const query = families
    .map((family) => `family=${family.replace(/ /g, '+')}:wght@400;500;600;700`)
    .join('&');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  link.setAttribute('data-partner-font', '1');
  document.head.appendChild(link);
};

// Resolve and apply the partner theme (if any) BEFORE React renders. Always
// resolves to a usable theme; on any failure it falls back to the house brand.
export const ensurePartnerTheme = async () => {
  if (typeof window === 'undefined') {
    return defaultTheme;
  }
  const slug = getPartnerSlug();
  if (!slug) {
    setResolvedTheme(defaultTheme);
    return defaultTheme;
  }

  const partner = await fetchPartnerConfig(slug);
  const resolved = buildResolvedTheme(partner);

  if (resolved.slug) {
    applyCssVars(resolved);
    loadFonts([resolved.fontNames.body, resolved.fontNames.heading]);
  }
  setResolvedTheme(resolved);
  return resolved;
};

export default ensurePartnerTheme;
